export type Rng = () => number

export const createRng = (seed: number): Rng => {
    let state = seed >>> 0

    return (): number => {
        state = (state + 0x6D2B79F5) >>> 0

        let t = state

        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

export const hashString = (value: string): number => {
    let hash = 2166136261

    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i)
        hash = Math.imul(hash, 16777619)
    }

    return hash >>> 0
}

export const gaussian = (rng: Rng): number => {
    let u = 0
    let v = 0

    while (u === 0) u = rng()
    while (v === 0) v = rng()

    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}

export const between = (rng: Rng, min: number, max: number): number => min + (max - min) * rng()

export const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value))

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

export const smoothstep = (edge0: number, edge1: number, value: number): number => {
    const t = clamp((value - edge0) / (edge1 - edge0), 0, 1)

    return t * t * (3 - 2 * t)
}

export const gauss2 = (x: number, z: number, cx: number, cz: number, sigma: number): number => {
    const dx = x - cx
    const dz = z - cz

    return Math.exp(-(dx * dx + dz * dz) / (2 * sigma * sigma))
}

export type Noise2D = (x: number, y: number) => number

export const createNoise2D = (seed: number): Noise2D => {
    const rng = createRng(seed)
    const perm = new Uint8Array(256)
    const values = new Float32Array(256)

    for (let i = 0; i < 256; i++) {
        perm[i] = i
        values[i] = rng() * 2 - 1
    }

    for (let i = 255; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1))
        const swap = perm[i]

        perm[i] = perm[j]
        perm[j] = swap
    }

    const lattice = (ix: number, iy: number): number => values[perm[(perm[ix & 255] + iy) & 255]]

    return (x: number, y: number): number => {
        const x0 = Math.floor(x)
        const y0 = Math.floor(y)
        const fx = x - x0
        const fy = y - y0
        const sx = fx * fx * (3 - 2 * fx)
        const sy = fy * fy * (3 - 2 * fy)

        const a = lerp(lattice(x0, y0), lattice(x0 + 1, y0), sx)
        const b = lerp(lattice(x0, y0 + 1), lattice(x0 + 1, y0 + 1), sx)

        return lerp(a, b, sy)
    }
}

/**
 * Splits `total` into integer parts proportional to `weights`, summing exactly to `total`.
 */
export const allocate = (total: number, weights: Array<number>): Array<number> => {
    const sum = weights.reduce((acc, weight) => acc + weight, 0) || 1
    const raw = weights.map(weight => (weight / sum) * total)
    const parts = raw.map(value => Math.floor(value))

    let rest = total - parts.reduce((acc, value) => acc + value, 0)

    const order = raw
        .map((value, index) => ({ index, frac: value - Math.floor(value) }))
        .sort((a, b) => b.frac - a.frac)

    for (let i = 0; rest > 0 && order.length > 0; i = (i + 1) % order.length) {
        parts[order[i].index]++
        rest--
    }

    return parts
}
